import PayRun, { Adjustment, PayrunPayment } from "../models/PayRun";
import { PayrunPayment as PayrunPaymentOpenApi, PayrunBillableItem } from "./openApi";
import PaymentMethods from "../definitions/PaymentMethods";
import ExpenseAnalysis from "../models/ExpenseAnalysis";
import { Currencies } from "../definitions/constants";
import ApiService from "./ApiService";
import Utils from "../utils/Utils";
import { SystemErrors } from "../definitions/systemErrors";
import moment from "moment";

export default class PayRunService {
  public static async GetTeammemberPayableCharges(teammemberId: string, fromDate: string, toDate: string): Promise<PayrunBillableItem[] | null> {
    return new Promise((resolve, reject) => {
      ApiService.get(`/payrun/getTeammemberPayableCharges/${teammemberId}?fromDate=${fromDate}&toDate=${toDate}`, "")
        .then((result) => {
          if (!result.data) {
            resolve(null);
          } else {
            result.data.forEach((item: any) => {
              item.commitmentBillableItems.forEach((commitmentBillableItem: any) => {
                if (commitmentBillableItem.commitment) {
                  commitmentBillableItem.commitment.EndDate = this.stringToDate(commitmentBillableItem.commitment.EndDate);
                  commitmentBillableItem.commitment.StartDate = this.stringToDate(commitmentBillableItem.commitment.StartDate);
                }

                if (commitmentBillableItem.timesheets && commitmentBillableItem.timesheets.length) {
                  commitmentBillableItem.timesheets.forEach((timesheet: any) => {
                    timesheet.Date = moment(timesheet.Date).toDate();
                  });
                }
              });
            });
            resolve(result.data);
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public static getById(id: string): Promise<PayRun | null> {
    return new Promise((resolve, reject) => {
      ApiService.get(`/payrun/getById/${id}`, "")
        .then((payRunResult) => {
          if (!payRunResult.data) {
            resolve(null);
          } else {
            resolve(this.preparePayRun(payRunResult.data));
          }
        })
        .catch((payRunError) => {
          reject(payRunError);
        });
    });
  }
  public static getListByTeammemberId(teamMemberId: string): Promise<PayRun[]> {
    return new Promise((resolve, reject) => {
      ApiService.get(`/payrun/${teamMemberId}`, "")
        .then((payRunResult) => {
          if (!payRunResult.data) {
            resolve([]);
          } else {
            const retVal = payRunResult.data.map((doc: any) => {
              return this.preparePayRun(doc);
            });
            resolve(retVal);
          }
        })
        .catch((payRunError) => {
          reject(payRunError);
        });
    });
  }

  public static getListByTeammemberEmail(): Promise<PayRun[]> {
    return new Promise((resolve, reject) => {
      ApiService.get(`/payrun`, "")
        .then((payRunResult) => {
          if (!payRunResult.data) {
            resolve([]);
          } else {
            const retVal = payRunResult.data.map((doc: any) => {
              return this.preparePayRun(doc);
            });
            resolve(retVal);
          }
        })
        .catch((payRunError) => {
          reject(payRunError);
        });
    });
  }

  public static GetActualExpenseAnalysis(startDate: Date, endDate: Date, teammemberId: string): Promise<ExpenseAnalysis | null> {
    return new Promise((resolve, reject) => {
      endDate.setHours(endDate.getHours() + 24);
      startDate.setHours(startDate.getHours() - 24);
      ApiService.post(`/payrun/actualExpenseAnalysis`, { StartDate: startDate, EndDate: endDate, TeammemberId: teammemberId })
        .then((res: any) => {
          resolve(this.getResult(res));
        })
        .catch((payRunError) => {
          reject(payRunError);
        });
    });
  }
  public static GetForecastExpenseAnalysis(startDate: Date, endDate: Date, teammemberId: string): Promise<ExpenseAnalysis | null> {
    return new Promise((resolve, reject) => {
      ApiService.post(`/payrun/forecastExpenseAnalysis`, { StartDate: startDate, EndDate: endDate, TeammemberId: teammemberId })
        .then((res) => {
          resolve(this.getResult(res));
        })
        .catch((payRunError) => {
          reject(payRunError);
        });
    });
  }

  private static getResult(res: any) {
    if (!res.data || res.data.statusCode != SystemErrors.Authentication.Success || !res.data.value) {
      return null;
    } else {
      return res.data.value;
    }
  }
  public static preparePayRun(doc: any) {
    const item = {
      id: doc.id,
      ...doc,
    } as PayRun;

    if (!item.Adjustments) {
      item.Adjustments = [] as Adjustment[];
    }

    if (!(item.FromDate instanceof Date)) {
      item.FromDate = this.stringToDate(item.FromDate);
    }
    if (!(item.ToDate instanceof Date)) {
      item.ToDate = this.stringToDate(item.ToDate);
    }
    if (!(item.PaymentDate instanceof Date)) {
      item.PaymentDate = this.stringToDate(item.PaymentDate);
    }
    item.Items.forEach((payrunItems) => {
      payrunItems.Weeks.forEach((week) => {
        if (!(week.FromDate instanceof Date)) {
          week.FromDate = this.stringToDate(week.FromDate);
        }
        if (!(week.ToDate instanceof Date)) {
          week.ToDate = this.stringToDate(week.ToDate);
        }
      });
    });
    if (item.Payments) {
      item.Payments.forEach((payment) => {
        if (payment.PaymentDate) {
          payment.PaymentDate = this.stringToDate(payment.PaymentDate);
        }
      });
    }
    return item;
  }
  public static set(model: PayRun): Promise<PayRun> {
    return new Promise((resolve, reject) => {
      ApiService.post(`/payrun/admin`, model)
        .then((result) => {
          model.id = result.data;
          resolve(model);
        })
        .catch((setError) => reject(setError));
    });
  }

  private static stringToDate(value: any): Date {
    return Utils.vsDateToDatetime(value);
  }

  public static getPayRunExchangeCurrency(item: PayRun | null | undefined): string {
    if (item && item.ExchangeCurrency) {
      return item.ExchangeCurrency;
    }
    const defaultCurrency = Currencies.find((x) => x.isDefault);
    return defaultCurrency ? defaultCurrency.id : "";
  }

  public static async setPayments(id: string, model: PayrunPayment[]) {
    if (model) {
      model.forEach((item) => {
        item.PaymentDate = Utils.addTimezoneOffsetToStartDate(item.PaymentDate);
      });
    }
    return new Promise((resolve, reject) => {
      ApiService.post(`/payrun/setPayments/${id}`, model)
        .then((res) => {
          resolve(model);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  public static approveByTM(id: string): Promise<PayRun> {
    return new Promise((resolve, reject) => {
      ApiService.post(`/payrun/approveByTM/${id}`, null)
        .then((result) => {
          resolve(result.data);
        })
        .catch((setError) => reject(setError));
    });
  }

  public static getPaymentRefAndDateDescription(payments: PayrunPaymentOpenApi[] | undefined, exchangeCurrency: string | undefined): string {
    let refText = "";
    if (payments && payments.length) {
      refText += `<br/><table style="border: 1px solid; width: 100%;">
      <tr>
        <th>Reference</th>
        <th>Date</th>
        <th>Amount (AUD)</th>
        <th>Amount (${exchangeCurrency})</th>
      </tr>`;
      var exchangeCurrencyText = exchangeCurrency ? exchangeCurrency : "TMN";
      payments.forEach((payment, index) => {
        refText += `<tr>`;

        refText += `<td>`;
        if (payment.PaymentRefNo) {
          refText += `${payment.PaymentRefNo} `;
        }
        refText += `</td>`;

        refText += `<td>`;

        if (payment.PaymentDate) {
          refText += `${Utils.toVsDateFormat(new Date(payment.PaymentDate))} `;
        }
        refText += `</td>`;
        refText += `<td>`;
        if (payment.AUDTotalPay) {
          refText += `${Utils.moneyFormat(payment.AUDTotalPay)} AUD `;
        }
        refText += `</td>`;
        refText += `<td>`;
        if (payment.LCTotalPay) {
          refText += `${Utils.moneyFormat(payment.LCTotalPay)} ${exchangeCurrencyText} `;
        }
        refText += `</td>`;
        refText += `</tr>`;
      });
      var totalAUDPayment = payments.reduce((n, { AUDTotalPay }) => n + (AUDTotalPay ?? 0), 0);
      var totalLCPayment = payments.reduce((n, { LCTotalPay }) => n + (LCTotalPay ?? 0), 0);
      refText += `<tr>
  	<td><strong>Total payment</strong></td>
    <td></td>
    <td><strong>${totalAUDPayment} AUD</strong></td>
    <td><strong>${totalLCPayment} ${exchangeCurrencyText}</strong></td>
  </tr>`;
      refText += `</table><br/>`;
    }
    return refText;
  }

  public static createCryptoHtml(crypto: any) {
    var htmlText = "<div class='row pa-3 pb-5'>";
    htmlText += `<div class='pt-3 pb-0 col-md-12 col-sm-12 col-12'><span class='font-weight-black'>Payment method: Crypto currency</span></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Wallet address:</span> ${crypto.WalletAddress}</p></div>`;
    htmlText += "</div>";
    return htmlText;
  }
  public static createBankHtml(bank: any) {
    var htmlText = "<div class='row pa-3 pb-5'>";
    htmlText += `<div class='pt-3 pb-0 col-md-12 col-sm-12 col-12'><span class='font-weight-black'>Payment method: Bank account</span></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Country:</span> ${bank.Country}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Account Name:</span> ${bank.AccountName}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Account Number:</span> ${bank.AccountNumber}</p></div>`;
    htmlText += bank.BSB ? `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p><span class='font-weight-bold'>BSB:</span> ${bank.BSB}</p></div>` : "";
    htmlText += bank.ShebaNumber ? `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p>Sheba Number: ${bank.ShebaNumber}</p></div>` : "";
    htmlText += "</div>";
    return htmlText;
  }
  public static createWiseHtml(wise: any) {
    var htmlText = "<div class='row pa-3 pb-5'>";
    htmlText += `<div class='pt-3 pb-0 col-md-12 col-sm-12 col-12'><span class='font-weight-black'>Payment method: Wise account</span></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Full Name:</span> ${wise.FullName}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Email:</span> ${wise.Email}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Bank Name:</span> ${wise.BankName}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Account Number:</span> ${wise.AccountNumber}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Swift Code:</span> ${wise.SwiftCode}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Country:</span> ${wise.Country}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>City:</span> ${wise.City}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Post Code:</span> ${wise.PostCode}</p></div>`;
    htmlText += `<div class='pt-0 col-md-4 col-sm-4 col-12'><p class="mb-0"><span class='font-weight-bold'>Recipient Address:</span> ${wise.RecipientAddress}</p></div>`;
    htmlText += "</div>";
    return htmlText;
  }
  public static createPaypalHtml(paypal: any) {
    var htmlText = "<div class='row pa-3 pb-5'>";
    htmlText += `<div class='pt-3 pb-0 col-md-12 col-sm-12 col-12'><span class='font-weight-black'>Payment method: Paypal</span></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0">Email: ${paypal.Email}</p></div>`;
    htmlText += `<div class='pt-0 pb-0 col-md-4 col-sm-4 col-12'><p class="mb-0">Full Name: ${paypal.FullName}</p></div>`;
    htmlText += "</div>";
    return htmlText;
  }

  public static getPaymentMethodNameByNumber(number: any) {
    switch (number) {
      case PaymentMethods.CryptoCurrency:
        return "Crypto Currency";
      case PaymentMethods.BankAccount:
        return "Bank Account";
      case PaymentMethods.WiseAccount:
        return "Wise Account";
      case PaymentMethods.Paypal:
        return "Paypal";
      default:
        return "";
    }
  }
}
